import axios from 'axios';
import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addItem, removeItem, increaseQuantity, decreaseQuantity } from '../redux/cart';
import { ToastContainer, Zoom, toast } from 'react-toastify';
import { IoSearchCircle } from "react-icons/io5";
import { FaStar, FaRegStar, FaStarHalfAlt, FaArrowRight } from 'react-icons/fa';

function HomePage() {
    const [imgData, setImgData] = useState([]);
    const [productsByCategory, setProductsByCategory] = useState({});
    const [categoryList, setCategoryList] = useState([]);
    const [brandList, setBrandList] = useState([]);
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [error, setError] = useState(null);
    const cartItems = useSelector(state => state.cart.items);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

    const fetchImages = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/slider-images`);
            setImgData(response.data || []);
        } catch (err) {
            console.error("Failed to fetch slider images:", err);
        } finally {
            setIsLoading(false);
        }
    }, [API_URL]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/categorydata`);
            setCategoryList(response.data || []);
        } catch (err) {
            console.error("Failed to fetch categories:", err);
        }
    }, [API_URL]);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/product-list`);
            const groupedProducts = response.data.reduce((acc, product) => {
                const category = product.category;
                if (!acc[category]) acc[category] = [];
                acc[category].push(product);
                return acc;
            }, {});
            setProductsByCategory(groupedProducts);
        } catch (err) {
            console.error("Failed to fetch products:", err);
        }
    }, [API_URL]);

    const fetchBrands = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/branddata`);
            setBrandList(response.data || []);
        } catch (err) {
            console.error("Failed to fetch brands:", err);
        }
    }, [API_URL]);

    const fetchFeaturedProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/featured-products`);
            setFeaturedProducts(response.data || []);
        } catch (err) {
            console.error("Failed to fetch featured products:", err);
        }
    }, [API_URL]);

    useEffect(() => {
        let isMounted = true;
        (async () => {
            setIsLoading(true);
            setError(null);
            try {
                await Promise.all([
                    fetchImages(),
                    fetchCategories(),
                    fetchProducts(),
                    fetchBrands(),
                    fetchFeaturedProducts()
                ]);
            } catch (e) {
                if (isMounted) setError('Failed to load some homepage data');
            } finally {
                if (isMounted) setIsLoading(false);
            }
        })();
        return () => { isMounted = false; };
    }, [fetchImages, fetchCategories, fetchProducts, fetchBrands, fetchFeaturedProducts]);

    const cartCount = useMemo(() => cartItems.length, [cartItems]);

    // Fallback tiny transparent placeholder (shared by category & brand items)
    const squareImgFallback = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    const handleSquareImgError = (e) => {
        if (!e.currentTarget.dataset.fallback) {
            e.currentTarget.dataset.fallback = '1';
            e.currentTarget.src = squareImgFallback;
            e.currentTarget.style.opacity = '0.5';
        }
    };

    const toastStyle = {
        width: '450px',
        border: '1px solid #bbbbbb',
        boxShadow: '0 0 21px #ed3237',
        textAlign: 'center',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: '40px',
        backgroundColor: '#ed3237',
        fontSize: '18px'
    };

    const handleAddToCart = (product) => {
        dispatch(addItem(product));
        toast.success('item added to cart', {
            autoClose: 500,
            hideProgressBar: true,
            transition: Zoom,
            icon: false,
            closeButton: false,
            style: toastStyle
        });
    };

    const handleInc = (id) => dispatch(increaseQuantity(id));
    const handleDec = (id) => dispatch(decreaseQuantity(id));

    return (
        <Fragment>
            <div className="container-fluid g-0 p-0">
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                        {isLoading ? (
                            <div className="w-100 position-relative" style={{height:'280px'}}>
                                <div className="skeleton-block w-100 h-100" style={{borderRadius:'4px'}}></div>
                            </div>
                        ) : (
                            <div id="carouselExampleAutoplaying" className="carousel slide bg-white rounded-0 border-0 m-0 p-0 w-100" data-bs-ride="carousel" data-bs-interval="3500" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                <div className="carousel-inner bg-white rounded-0 border-0 m-0 p-0 w-100" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                    <div className="carousel-item active bg-white rounded-0 border-0 m-0 p-0 w-100" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                        <img loading="lazy" src={imgData[0] && `${API_URL}/${imgData[0].image1}`} className="d-block w-100 rounded-0 border-0" alt="Slide 1" style={{ borderRadius: 0, border: 'none', margin: 0 }} onError={(e)=>{e.currentTarget.style.opacity='0.4';}} />
                                    </div>
                                    <div className="carousel-item bg-white rounded-0 border-0 m-0 p-0 w-100" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                        <img loading="lazy" src={imgData[0] && `${API_URL}/${imgData[0].image2}`} className="d-block w-100 rounded-0 border-0" alt="Slide 2" style={{ borderRadius: 0, border: 'none', margin: 0 }} onError={(e)=>{e.currentTarget.style.opacity='0.4';}} />
                                    </div>
                                    <div className="carousel-item bg-white rounded-0 border-0 m-0 p-0 w-100" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                        <img loading="lazy" src={imgData[0] && `${API_URL}/${imgData[0].image3}`} className="d-block w-100 rounded-0 border-0" alt="Slide 3" style={{ borderRadius: 0, border: 'none', margin: 0 }} onError={(e)=>{e.currentTarget.style.opacity='0.4';}} />
                                    </div>
                                    <div className="carousel-item bg-white rounded-0 border-0 m-0 p-0 w-100" style={{ backgroundColor: '#ffffff', borderRadius: 0, margin: 0, padding: 0 }}>
                                        <img loading="lazy" src={imgData[0] && `${API_URL}/${imgData[0].image4}`} className="d-block w-100 rounded-0 border-0" alt="Slide 4" style={{ borderRadius: 0, border: 'none', margin: 0 }} onError={(e)=>{e.currentTarget.style.opacity='0.4';}} />
                                    </div>
                                </div>
                                <button className="carousel-control-prev" type="button" data-bs-target="#carouselExampleAutoplaying" data-bs-slide="prev">
                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                </button>
                                <button className="carousel-control-next" type="button" data-bs-target="#carouselExampleAutoplaying" data-bs-slide="next">
                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className='cat-strip'>
                <label>TOP CATEGORIES</label>
                <Link to="/categories">View All <FaArrowRight style={{ fontSize: '10px', marginLeft: '4px' }} /></Link>
            </div>
            {error && <div className='alert alert-warning small rounded-0 m-2'>{error}</div>}
            <div className='plist' aria-label='Top categories list'>
                {isLoading ? (
                    Array.from({length:8}).map((_,i)=>(
                        <div key={i} className='text-center d-flex flex-column align-items-center justify-content-start' style={{width:'100px'}} aria-hidden='true'>
                            <div className='skeleton-block mb-1' style={{width:'100px',height:'100px',borderRadius:'8px'}}></div>
                            <div className='skeleton-block' style={{width:'70px',height:'14px',borderRadius:'4px'}}></div>
                        </div>
                    ))
                ) : categoryList.length > 0 ? (
                    categoryList.map((cat, index) => (
                        <Link to={`/products-by-cat/${cat.name}`} key={index} aria-label={`View category ${cat.name}`} title={cat.name}>
                            <img
                                loading="lazy"
                                src={`${API_URL}/${cat.image}`}
                                alt={cat.name}
                                onError={handleSquareImgError}
                            />
                            <h4 className='mt-1 mb-0'>{cat.name}</h4>
                        </Link>
                    ))
                ) : (
                    <div className='text-muted small py-2' role='status'>No categories available.</div>
                )}
            </div>
            <div className='cat-strip'>
                <label>TOP BRANDS</label>
                <Link to="/brand-list">View All <FaArrowRight style={{ fontSize: '10px', marginLeft: '4px' }} /></Link>
            </div>
            <div className='plist' aria-label='Top brands list'>
                {isLoading ? (
                    Array.from({length:8}).map((_,i)=>(
                        <div key={i} className='text-center d-flex flex-column align-items-center justify-content-start' style={{width:'100px'}} aria-hidden='true'>
                            <div className='skeleton-block mb-1' style={{width:'100px',height:'100px',borderRadius:'8px'}}></div>
                            <div className='skeleton-block' style={{width:'70px',height:'14px',borderRadius:'4px'}}></div>
                        </div>
                    ))
                ) : brandList.length > 0 ? (
                    brandList.map((brand, index) => (
                        <Link
                            to={`/products-by-brand/${brand.name}`}
                            key={index}
                            aria-label={`View brand ${brand.name}`}
                            title={brand.name}
                        >
                            <img
                                loading="lazy"
                                src={`${API_URL}/${brand.image}`}
                                alt={brand.name}
                                onError={handleSquareImgError}
                            />
                            <h4 className='mt-1 mb-0'>{brand.name}</h4>
                        </Link>
                    ))
                ) : (
                    <div className='text-muted small py-2' role='status'>No brands available.</div>
                )}
            </div>
            <div className='cat-strip'>
                <label>BEST SELLERS</label>
            </div>
            <div className="container-fluid" aria-label='Best sellers product list'>
                <div className="row">
                    {isLoading ? (
                        Array.from({length:6}).map((_,i)=>(
                            <div className='col-6 prod-list text-center border' key={i} aria-hidden='true'>
                                <div className='skeleton-block w-100' style={{height:'150px'}}></div>
                                <div className='skeleton-block mx-auto mt-2' style={{width:'80%',height:'16px'}}></div>
                                <div className='skeleton-block mx-auto mt-2' style={{width:'40%',height:'14px'}}></div>
                                <div className='skeleton-block mx-auto mt-2' style={{width:'60%',height:'32px',borderRadius:'4px'}}></div>
                            </div>
                        ))
                    ) : featuredProducts.length > 0 ? (
                        featuredProducts.slice(0, 6).map((item, idx) => {
                            const cartEntry = cartItems.find(ci => ci._id === item._id || ci.name === item.name);
                            const inCart = !!cartEntry;
                            const original = Number(item.originalPrice || item.price || 0);
                            const price = Number(item.price || 0);
                            const hasDiscount = original > price && original > 0;
                            const discountPct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
                            const ratingVal = Number(item.rating || 0);
                            const stars = [];
                            for (let i = 1; i <= 5; i++) {
                                if (ratingVal >= i) stars.push(<FaStar key={i} className='text-warning' />); else if (ratingVal >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className='text-warning' />); else stars.push(<FaRegStar key={i} className='text-warning' />);
                            }
                            return (
                                <div className='col-6 prod-list text-center border position-relative' key={item._id || idx}>
                                    <Link to={`/product/${item.name}`} className='d-block w-100 position-relative' aria-label={`View product ${item.name}`}>
                                        {hasDiscount && (
                                            <span className='discount-badge' aria-label={`${discountPct}% discount`}>{discountPct}%</span>
                                        )}
                                        <img
                                            loading="lazy"
                                            src={`${API_URL}/${item.image1}`}
                                            alt={item.name}
                                            className='w-100'
                                            onError={(e) => {
                                                if (!e.currentTarget.dataset.fallback) {
                                                    e.currentTarget.dataset.fallback = '1';
                                                    e.currentTarget.src = squareImgFallback;
                                                    e.currentTarget.style.opacity = '0.5';
                                                }
                                            }}
                                        />
                                        <label>{item.name.toUpperCase()}</label>
                                    </Link>
                                    <div className='d-flex justify-content-center align-items-center gap-1 mb-1' style={{ minHeight: '20px' }}>
                                        {stars}
                                    </div>
                                    <div className='d-flex justify-content-between align-items-center mb-1 w-100' style={{fontSize:'13px',textAlign:'left'}}>
                                        <div className='d-flex align-items-center flex-wrap'>
                                            <strong className='text-danger me-1' style={{fontSize:'15px'}}>₹{price}</strong>
                                            {hasDiscount && <span className='text-muted text-decoration-line-through' style={{ fontSize: '12px' }}>₹{original}</span>}
                                        </div>
                                        {item.quantity && item.unit ? (
                                            <span className='text-muted small'>{item.quantity}{item.unit}</span>
                                        ) : <span className='invisible small'>.</span>}
                                    </div>
                                    {!inCart ? (
                                        <button onClick={() => handleAddToCart(item)}>ADD TO CART</button>
                                    ) : (
                                        <div className='qty-bar'>
                                            <button type='button' aria-label='Decrease quantity' onClick={() => handleDec(cartEntry._id)}>-</button>
                                            <span className='qty-mid'>{cartEntry.quantity}</span>
                                            <button type='button' aria-label='Increase quantity' onClick={() => handleInc(cartEntry._id)}>+</button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className='col-12 text-center text-muted small py-3' role='status'>No best sellers available.</div>
                    )}
                </div>
            </div>
            <div className="container-fluid g-0 pad-bot-70px">
                {Object.keys(productsByCategory).map((category, index) => (
                    <div key={index}>
                        <div className='cat-strip'>
                            <label>{category.toUpperCase()}</label>
                            <Link to={`/products-by-cat/${category}`}>View All <FaArrowRight style={{ fontSize: '10px', marginLeft: '4px' }} /></Link>
                        </div>
                        <div className='container-fluid'>
                            <div className="row">
                                {isLoading ? (
                                    Array.from({length:6}).map((_,i)=>(
                                        <div className='col-6 prod-list text-center border' key={i}>
                                            <div className='skeleton-block w-100' style={{height:'150px'}}></div>
                                            <div className='skeleton-block mx-auto mt-2' style={{width:'80%',height:'16px'}}></div>
                                            <div className='skeleton-block mx-auto mt-2' style={{width:'40%',height:'14px'}}></div>
                                            <div className='skeleton-block mx-auto mt-2' style={{width:'60%',height:'32px',borderRadius:'4px'}}></div>
                                        </div>
                                    ))
                                ) : (
                                    productsByCategory[category]?.slice(0, 6).map((item, idx) => {
                                        const original = Number(item.originalPrice || item.price || 0);
                                        const price = Number(item.price || 0);
                                        const hasDiscount = original > price && original > 0;
                                        const discountPct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
                                        const ratingVal = Number(item.rating || 0);
                                        const stars = [];
                                        for (let i = 1; i <= 5; i++) {
                                            if (ratingVal >= i) stars.push(<FaStar key={i} className='text-warning' />); else if (ratingVal >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className='text-warning' />); else stars.push(<FaRegStar key={i} className='text-warning' />);
                                        }
                                        const cartEntry = cartItems.find(ci => ci._id === item._id || ci.name === item.name);
                                        const inCart = !!cartEntry;
                                        return (
                                            <div className='col-6 prod-list text-center border position-relative' key={idx}>
                                                <Link to={`/product/${item.name}`} className='d-block w-100 position-relative'>
                                                    {hasDiscount && (
                                                        <span className='discount-badge' aria-label={`${discountPct}% discount`}>{discountPct}%</span>
                                                    )}
                                                    <img loading="lazy" src={`${API_URL}/${item.image1}`} alt={item.name} className='w-100' onError={(e) => { e.currentTarget.style.opacity = '0.4'; }} />
                                                    <label>{item.name.toUpperCase()}</label>
                                                </Link>
                                                <div className='d-flex justify-content-center align-items-center gap-1 mb-1' style={{ minHeight: '20px' }}>
                                                    {stars}
                                                </div>
                                                <div className='d-flex justify-content-between align-items-center mb-1 w-100' style={{fontSize:'13px',textAlign:'left'}}>
                                                    <div className='d-flex align-items-center flex-wrap'>
                                                        <strong className='text-danger me-1' style={{fontSize:'15px'}}>₹{price}</strong>
                                                        {hasDiscount && <span className='text-muted text-decoration-line-through' style={{ fontSize: '12px' }}>₹{original}</span>}
                                                    </div>
                                                    {item.quantity && item.unit ? (
                                                        <span className='text-muted small'>{item.quantity}{item.unit}</span>
                                                    ) : <span className='invisible small'>.</span>}
                                                </div>
                                                {!inCart ? (
                                                    <button onClick={() => handleAddToCart(item)}>ADD TO CART</button>
                                                ) : (
                                                    <div className='qty-bar'>
                                                        <button type='button' aria-label='Decrease quantity' onClick={() => handleDec(cartEntry._id)}>-</button>
                                                        <span className='qty-mid'>{cartEntry.quantity}</span>
                                                        <button type='button' aria-label='Increase quantity' onClick={() => handleInc(cartEntry._id)}>+</button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* <Link to='/search' className='search-icon'><IoSearchCircle/></Link> */}
            <ToastContainer position='top-center' />
        </Fragment>
    );
}

export default HomePage;
